import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
// Direct module imports — src/firebase/index.js is NOT a barrel, it is a stray
// second Firebase init hardcoded to config.dev. Do not import from it.
import { createUserDocument } from '../../firebase/User';
import { doSignOut } from '../../firebase/Auth';
import { COPY } from '../../contracts/copyMap';
import EmptyState from '../oracle/EmptyState';
import LogoSpinner from '../oracle/LogoSpinner';

// Shown by AuthGate when the user is signed in but has NO Firestore user doc.
//
// This state used to be a permanent full-screen spinner: AuthGate conflated
// "still loading" with "resolved, and there is no doc", so ~100 accounts whose
// client-side write never landed could never use the app at all. This component
// is the client half of the fix (the server half is the onAuthUserCreate
// trigger + backfillMissingUserDocs).
//
//   mount
//     │ wait GRACE_MS                     absorbs the legitimate sub-second
//     │   (spinner)                       post-signup window AND the ~1s the
//     │                                   onCreate trigger needs to provision
//     │ doc arrived? ── yes ──> unmount (AuthGate re-renders with userData)
//     │ no
//     ▼
//   createUserDocument()  <- rules already allow `create` for self
//     │ ok    ──> live snapshot delivers the doc ──> unmount
//     └ fail  ──> real error surface: RETRY + SIGN OUT   (never a spinner)
//
// The grace window is what keeps this from being a flash-of-error during a
// normal signup. Past it, doing nothing is strictly worse than trying.
export const GRACE_MS = 3000;

const UserDocRecovery = () => {
  const { currentUser } = useAuth();
  const [phase, setPhase] = useState('waiting'); // waiting | healing | failed
  // Guards against double-invocation (React 18 StrictMode double-effect, and a
  // re-render landing mid-heal). A duplicate create() would be a wasted write.
  const attempted = useRef(false);

  const heal = useCallback(async () => {
    if (!currentUser) return;
    setPhase('healing');
    try {
      await createUserDocument(currentUser, {
        email: currentUser.email,
        display_name: currentUser.displayName,
      });
      // Success is observed via the live snapshot in UserContext, which flips
      // userData non-null and unmounts this component. Nothing to do here.
    } catch (err) {
      console.error('UserDocRecovery: self-heal failed', err);
      setPhase('failed');
    }
  }, [currentUser]);

  useEffect(() => {
    if (attempted.current) return undefined;
    const timer = setTimeout(() => {
      attempted.current = true;
      heal();
    }, GRACE_MS);
    return () => clearTimeout(timer);
  }, [heal]);

  const retry = useCallback(() => {
    attempted.current = true;
    heal();
  }, [heal]);

  if (phase === 'failed') {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <EmptyState
          headline={COPY.RECOVERY_FAILED_HEADLINE}
          subtitle={COPY.RECOVERY_FAILED_SUBTITLE}
          ctaLabel={COPY.RETRY_CTA}
          onCta={retry}
          secondaryCtaLabel={COPY.SIGN_OUT_CTA}
          onSecondaryCta={doSignOut}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <LogoSpinner />
    </div>
  );
};

export default UserDocRecovery;
